/**
 * What this chain can do, and what it has done.
 *
 * Every explorer shows the second half. This shows both, because the runtime
 * declares its whole dispatchable surface in the same metadata the decoder
 * reads — so a call nobody has ever made is a row here rather than an absence.
 *
 * That gap is the point. `ReversibleTransfers`, `TechReferenda` and `Vesting`
 * are shipped, documented and untouched, and "this chain has governance nobody
 * has used" is a different statement from "this chain has no governance". The
 * unused rows keep their documentation, straight from the chain.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import type { CallIndex } from '../api/generated/CallIndex'
import { RequestFailed, fetchCalls } from '../api/rest'
import { ago, height as fmtHeight } from '../lib/format'
import { href } from '../lib/routes'

export function CallsIndex({ chain }: { chain: string }) {
  const [index, setIndex] = useState<CallIndex | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showUnused, setShowUnused] = useState(true)

  useEffect(() => {
    const controller = new AbortController()
    setIndex(null)
    setError(null)
    fetchCalls(chain, controller.signal)
      .then(setIndex)
      .catch((e: unknown) => {
        if (controller.signal.aborted) return
        setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
      })
    return () => controller.abort()
  }, [chain])

  const rows = index ? index.calls.filter((c) => showUnused || c.calls > 0) : []

  return (
    <section className="panel" style={{ marginBottom: 26 }}>
      <div className="panel-head">
        <h2 className="panel-title">Calls</h2>
        {index && (
          <span className="eyebrow">
            {index.used} of {index.calls.length} used
            {index.spec_version !== null && ` · runtime v${index.spec_version}`}
          </span>
        )}
      </div>

      {error && <p className="empty">{error}</p>}
      {!error && index === null && <p className="empty">Reading the runtime…</p>}

      {index !== null && (
        <>
          {/* The unused rows are the interesting half, so they are on by
              default — but somebody reading the activity wants them gone, and
              that is one click rather than a different page. */}
          <div className="pager" style={{ borderTop: 0, borderBottom: 'var(--rule)' }}>
            <button
              className="segmented panel-button"
              aria-pressed={showUnused}
              onClick={() => setShowUnused(!showUnused)}
            >
              {showUnused ? '✓ Showing unused' : 'Show unused'}
            </button>
          </div>

          <div className="scroll-x">
            <table className="board">
              <caption className="visually-hidden">
                Every dispatchable the runtime declares, with how often it has been used
              </caption>
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col" className="left">
                    Call
                  </th>
                  <th scope="col">Dispatched</th>
                  <th scope="col">Signers</th>
                  <th scope="col">Failed</th>
                  <th scope="col" className="event-when">
                    Last seen
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr
                    key={`${c.pallet}.${c.call}`}
                    className={c.calls === 0 ? 'call-unused' : undefined}
                  >
                    {/* The two bytes this call encodes as. Reading down the
                        column is how you see a pallet was inserted rather than
                        appended, which renumbers everything after it. */}
                    <td className="numeral">
                      <span className="runtime-index">
                        {c.pallet_index}.{c.call_index}
                      </span>
                    </td>
                    <td className="left">
                      <div className="call-cell">
                        {c.calls > 0 ? (
                          <Link to={href({ chain, call: [c.pallet, c.call] })}>
                            <code>
                              {c.pallet}::{c.call}
                            </code>
                          </Link>
                        ) : (
                          <code>
                            {c.pallet}::{c.call}
                          </code>
                        )}
                        {c.signature && <span className="runtime-type">({c.signature})</span>}
                        {c.docs && <span className="runtime-doc">{c.docs}</span>}
                      </div>
                    </td>
                    <td className="numeral">
                      {c.calls === 0 ? (
                        <span className="runtime-modifier">never</span>
                      ) : (
                        fmtHeight(c.calls)
                      )}
                    </td>
                    <td className="numeral">
                      {/* Unsigned is not "nobody": an inherent is the block
                          author's, and on this chain most traffic is unsigned. */}
                      {c.calls === 0 ? '—' : c.signers > 0 ? fmtHeight(c.signers) : 'unsigned'}
                    </td>
                    <td className="numeral">
                      {c.failed > 0 ? (
                        <span className="chip chip-failed">{c.failed}</span>
                      ) : c.calls === 0 ? (
                        '—'
                      ) : (
                        '0'
                      )}
                    </td>
                    <td className="numeral event-when" style={{ color: 'var(--text-muted)' }}>
                      {c.last_at
                        ? ago(c.last_at)
                        : c.last_height
                          ? `#${fmtHeight(c.last_height)}`
                          : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="panel-note">
            The declared surface comes from the runtime's own metadata; the counts come from this
            observer's extrinsic index, over blocks{' '}
            {index.indexed_from !== null && index.indexed_to !== null
              ? `${fmtHeight(index.indexed_from)} to ${fmtHeight(index.indexed_to)}`
              : 'it has read'}
            . A call showing <em>never</em> has not been dispatched in that range — which for a
            chain this young is most of them, and is the more interesting half of the table.
            Documentation is the runtime's, not ours.
          </p>
        </>
      )}
    </section>
  )
}
